import { useCallback, useEffect, useRef, useState } from 'react';
import { api, CallHistoryItem, CallHistoryFilter } from '../services/api';

type HistoryState = {
  token: string;
  // 9.4.10 / D-635 — süzgeç de kimliğin parçası. Sekme değişince liste hesap
  // değişmiş gibi baştan kuruluyor: yoksa "hepsi"nin satırları bir kare boyunca
  // cevapsız sekmesinde görünürdü.
  filter: CallHistoryFilter;
  items: CallHistoryItem[];
  loading: boolean;
  refreshing: boolean;
  loadingMore: boolean;
  failed: boolean;
  moreFailed: boolean;
};

type RequestScope = {
  token: string;
  filter: CallHistoryFilter;
  active: boolean;
  generation: number;
  firstPending: boolean;
  pagePending: boolean;
  moreFailed: boolean;
  next: string | null;
};

function initialState(token: string, filter: CallHistoryFilter): HistoryState {
  return { token, filter, items: [], loading: true, refreshing: false, loadingMore: false, failed: false, moreFailed: false };
}

const ayniKapsam = (scope: RequestScope, token: string, filter: CallHistoryFilter) =>
  scope.token === token && scope.filter === filter;

function uniqueItems(items: CallHistoryItem[]) {
  const seen = new Set<number>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

// Salt-okunur geçmiş. Yenileme ve hesap değişimi eski sayfa isteklerini hükümsüz
// kılar; hata mevcut satırları silmez ve sayfa hatası açık yeniden deneme bekler.
export function useCallHistory(token: string, filter: CallHistoryFilter = 'all') {
  const [state, setState] = useState<HistoryState>(() => initialState(token, filter));
  const scopeRef = useRef<RequestScope | null>(null);

  const isCurrent = useCallback((scope: RequestScope, generation: number) => (
    scope.active && scopeRef.current === scope && scope.generation === generation
  ), []);

  const fetchFirst = useCallback(async (scope: RequestScope, refreshing: boolean) => {
    if (!scope.active || scopeRef.current !== scope || scope.firstPending) return;
    const generation = ++scope.generation;
    scope.firstPending = true;
    scope.pagePending = false;
    scope.moreFailed = false;
    setState((previous) => ({
      ...(ayniKapsam(scope, previous.token, previous.filter) ? previous : initialState(scope.token, scope.filter)),
      loading: !refreshing,
      refreshing,
      loadingMore: false,
      failed: false,
      moreFailed: false,
    }));
    try {
      const result = await api.getCallHistory(scope.token, null, undefined, scope.filter);
      if (!isCurrent(scope, generation)) return;
      scope.next = result.next;
      setState((previous) => ({ ...previous, items: uniqueItems(result.calls) }));
    } catch {
      if (!isCurrent(scope, generation)) return;
      setState((previous) => ({ ...previous, failed: true }));
    } finally {
      if (isCurrent(scope, generation)) {
        scope.firstPending = false;
        setState((previous) => ({ ...previous, loading: false, refreshing: false }));
      }
    }
  }, [isCurrent]);

  const fetchMore = useCallback(async (scope: RequestScope, explicitRetry = false) => {
    if (!scope.active || scopeRef.current !== scope || scope.firstPending || scope.pagePending
      || !scope.next || (scope.moreFailed && !explicitRetry)) return;
    const generation = scope.generation;
    const cursor = scope.next;
    // Ref kilidi aynı render içindeki birden çok onEndReached çağrısını da keser.
    scope.pagePending = true;
    scope.moreFailed = false;
    setState((previous) => ({ ...previous, loadingMore: true, moreFailed: false }));
    try {
      const result = await api.getCallHistory(scope.token, cursor, undefined, scope.filter);
      if (!isCurrent(scope, generation)) return;
      scope.next = result.next;
      setState((previous) => ({ ...previous, items: uniqueItems([...previous.items, ...result.calls]) }));
    } catch {
      if (!isCurrent(scope, generation)) return;
      scope.moreFailed = true;
      setState((previous) => ({ ...previous, moreFailed: true }));
    } finally {
      if (isCurrent(scope, generation)) {
        scope.pagePending = false;
        setState((previous) => ({ ...previous, loadingMore: false }));
      }
    }
  }, [isCurrent]);

  useEffect(() => {
    const scope: RequestScope = {
      token, filter, active: true, generation: 0, firstPending: false, pagePending: false, moreFailed: false, next: null,
    };
    scopeRef.current = scope;
    setState(initialState(token, filter));
    fetchFirst(scope, false);
    return () => {
      scope.active = false;
      if (scopeRef.current === scope) scopeRef.current = null;
    };
  }, [token, filter, fetchFirst]);

  const refresh = useCallback(async () => {
    const scope = scopeRef.current;
    if (scope && ayniKapsam(scope, token, filter)) await fetchFirst(scope, true);
  }, [token, filter, fetchFirst]);
  const retry = useCallback(async () => {
    const scope = scopeRef.current;
    if (scope && ayniKapsam(scope, token, filter)) await fetchFirst(scope, false);
  }, [token, filter, fetchFirst]);
  const loadMore = useCallback(async () => {
    const scope = scopeRef.current;
    if (scope && ayniKapsam(scope, token, filter)) await fetchMore(scope);
  }, [token, filter, fetchMore]);
  const retryMore = useCallback(async () => {
    const scope = scopeRef.current;
    if (scope && ayniKapsam(scope, token, filter)) await fetchMore(scope, true);
  }, [token, filter, fetchMore]);

  // Yeni hesabın ya da yeni sekmenin ilk render'ında öncekinin satırlarını bir
  // kare bile sunma.
  const visible = state.token === token && state.filter === filter
    ? state : initialState(token, filter);
  return {
    items: visible.items,
    loading: visible.loading,
    refreshing: visible.refreshing,
    loadingMore: visible.loadingMore,
    failed: visible.failed,
    moreFailed: visible.moreFailed,
    refresh,
    retry,
    loadMore,
    retryMore,
  };
}

export default useCallHistory;
